import { useEffect, useState, useContext, useRef } from "react";
import { createBrowserRouter, useNavigate, useParams } from "react-router";
import { usePostHog } from '@posthog/react';

import { AppContext } from '../../context.js';
import { Chat as ChatIcon } from "../../files/icons/index.js";
import { Status, Button } from "../components/component.js";
import Loading from "../components/loading.js";
import axios from "axios";

import { DatetimeToDate, DateToTimestamp, DateToFormat, calculateAge } from "../../utils/datetime.js";

import Chat from "../components/chat.js";

function Referral() {
  const context = useContext(AppContext);
  const posthog = usePostHog();

  let { id } = useParams();

  let referralID = id;

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [chatTabSelected, setChatTabSelected] = useState("contact"); // contact, actions

  const [referral, setReferral] = useState(null);
  const [settings, setSettings] = useState(null);
  const [template, setTemplate] = useState(null);

  // Referral status change confirmation
  const [changeReferralStatusModalVisible, setChangeReferralStatusModalVisible] = useState(false);
  const [changeReferralStatusModalData, setChangeReferralStatusModalData] = useState(null);
  const [selectedReasonID, setSelectedReasonID] = useState(null);
  const [otherReasonText, setOtherReasonText] = useState("");

  useEffect(() => {
    let u = context.addSpiner();
    axios.all([
      axios.post('/api/admin/referrals', {
        filter: {
          id: [referralID]
        }
      }),
      axios.get('/api/admin/settings', {
      }),
    ]).then(axios.spread((referrals, settings) => {
      setReferral(referrals.data.referrals[0]);
      setSettings(settings.data);

      for (let i = 0; i < settings.data.referralTemplates.length; i++) {
        if (settings.data.referralTemplates[i].id === referrals.data.referrals[0].templateID) {
          setTemplate(settings.data.referralTemplates[i]);
          break;
        }
      }

      setLoading(false);
    })).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function renderPages() {
    return referral.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function renderLine(pageID, line, isLastLine) {
    // Line example
    //[{"code": "firstName", "size": 6}, {"code": "lastName", "size": 6}]

    return (
      <div key={"page_"+pageID+"_line_"+line.id} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = referral.fields.find((f) => f.code === lineField.code);

          return (
            <div key={"page_"+pageID+"_line_"+line.id+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div className="typo-strong-150">{field.title}:</div>
              {field.answers.length === 0 ? (
                <div className="typo-normal-150 w-100">n/a</div>
              ) : field.type !== "date" ? (
                <div className="typo-normal-150 w-100">{field.answers[0] ? field.answers[0] : "n/a"}</div>
              ) : (
                <div className="typo-normal-150 w-100">{DateToFormat(DatetimeToDate(field.answers[0]), context.dateFormat)}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function changeReferralStatus(status) {
    if (status.reasons.length > 0) {
      setChangeReferralStatusModalData(status);
      setSelectedReasonID(null);
      setOtherReasonText("");
      setChangeReferralStatusModalVisible(true);
      return;
    }

    updateReferralStatus(status.id, null, null);
  }

  function changeReferralStatusConfirm(statusID, reasonID, reasonNote) {
    setChangeReferralStatusModalVisible(false);
    setChangeReferralStatusModalData(null);
    setSelectedReasonID(null);
    setOtherReasonText("");
    updateReferralStatus(statusID, reasonID, reasonNote);
  }

  function updateReferralStatus(statusID, reasonID, reasonNote) {
    let u = context.addSpiner();
    axios.post('/api/admin/referrals/updateStatus', {
      id: referral.id,
      statusID: statusID,
      reasonID: reasonID,
      reasonNote: reasonNote,
    }).then((response) => {
      posthog?.capture('referral_status_updated', {
        referral_id: referral.id,
        status_id: response.data.statusID,
        status_name: response.data.statusName,
        status_type: response.data.statusType,
        reason_id: response.data.statusReasonID,
        reason_name: response.data.statusReasonName,
      });
      setReferral({ ...referral,
        statusType: response.data.statusType,
        statusID: response.data.statusID,
        statusName: response.data.statusName,
        statusReasonID: response.data.statusReasonID,
        statusReasonName: response.data.statusReasonName,
        statusReasonNote: response.data.statusReasonNote,

        closedAt: response.data.closedAt,
        closedBy: response.data.closedBy,
        closedByName: response.data.closedByName,
      });
    }).catch((e) => {
      posthog?.capture('referral_status_update_failed', {
        referral_id: referral.id,
        status_id: statusID,
        reason_id: reasonID,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function renderReferralStatus() {
    const currentStatus = template.statuses.find((ps) => ps.id === referral.statusID);

    return (
      <Status color={currentStatus.color} muted={true} size={'small'} text={referral.statusName} subtext={referral.statusReasonName} />
    );
  }

  if (loading) {
    return (
      <div className="container">
        <div className="row">
          <div className="col-xs-12">
            <Loading />
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {changeReferralStatusModalVisible && (
        <div className="fixed-bottom modal-bg" onClick={(e) => setChangeReferralStatusModalVisible(false)}>
          <div className="container" style={{paddingTop: '5%'}}>
            <div className="row">
              <div className="col-xs-6 col-xs-offset-3">
                <div className="card shadowmore" onClick={(e) => {e.stopPropagation()}}>
                  <div className="card-header">
                    <span className="typo-header-3xLarge">Confirm referral status change</span><br/>
                    <span className="typo-normal-175">Choose the correct reason and click on it</span>
                  </div>
                  <div className="card-content">
                    {changeReferralStatusModalData.reasons.map((reason, ri) => {
                      const isOther = reason.title === "Other";
                      const isSelected = selectedReasonID === reason.id;
                      return (
                        <div key={"reason_" + ri}>
                          <div className="side-item" onClick={() => {
                            if (isOther) {
                              setSelectedReasonID(reason.id);
                              setOtherReasonText("");
                            } else {
                              changeReferralStatusConfirm(changeReferralStatusModalData.id, reason.id, null);
                            }
                          }}>
                            <div>
                              <div className="typo-strong-175">{reason.title}</div>
                              <div className="typo-normal-150">{reason.description}</div>
                            </div>
                          </div>
                          {isOther && isSelected && (
                            <div className="px-3 pb-3">
                              <input
                                className="input sm typo-normal-150 w-100 mt-2"
                                type="text"
                                placeholder="Describe the reason..."
                                value={otherReasonText}
                                onChange={(e) => setOtherReasonText(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                              <button
                                className="button blue typo-body-medium mt-2"
                                disabled={!otherReasonText.trim()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  changeReferralStatusConfirm(changeReferralStatusModalData.id, reason.id, otherReasonText.trim());
                                }}
                              >
                                Confirm
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container">
        <div className="row mb-4">
          <div className="col-xs-12">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex justify-content-start align-items-center">
                <span className="typo-hero-small">Referral Overview</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row">

          {/* Chat */}
          {/* <div className="col-xs-12">
            <div className="card shadowmore mb-2">
              <div className="card-header sm">
                <div className="d-flex justify-content-start align-items-center">
                  <div className="d-flex justify-content-center align-items-center" style={{width: 20, height: 20}}>
                    <ChatIcon width={16} height={14} color={'grey'} />
                  </div>
                  <div className={"item typo-normal-150 ms-2 " + (chatTabSelected === "contact" ? "active" : "")}  onClick={() => setChatTabSelected("contact")} >Contact History</div>
                </div>
              </div>
              <div className="card-content sm">
                <Chat
                  referralID={referral.id}
                />
              </div>
            </div>
          </div> */}

          {/* Referral details and actions */}
          <div className="col-xs-12 mb-4">

            <div className="card shadowmore mb-4">
              <div className="card-header sm">
                <div className="row">
                  <div className="col-xs-6">
                    <div className="typo-strong-200">{template.title}</div>
                    <div className="typo-normal-150">{referral.selfReferral ? "Self Referral" : referral.partnerName}</div>
                  </div>
                  <div className="col-xs-6 text-end">
                    {renderReferralStatus()}
                    {!referral.closedAt ? (
                      <div className="typo-normal-150 mt-2">created at {DateToFormat(DatetimeToDate(referral.createdAt), context.dateFormat)} {DateToFormat(DatetimeToDate(referral.createdAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</div>
                    ) : (
                      <div className="typo-normal-150 mt-2">closed at {DateToFormat(DatetimeToDate(referral.closedAt), context.dateFormat)} {DateToFormat(DatetimeToDate(referral.closedAt), context.timeFormat === "us" ? "AMPM" : "HH:mm")}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-header sm">
                <div className="typo-strong-175">
                  Required Fields
                </div>
              </div>
              <div className="card-content sm">
                <div className="row mb-2">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">First Name:</div>
                    <div className="typo-normal-150 w-100">{referral.firstName ? referral.firstName : "N/A"}</div>
                  </div>
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Last Name:</div>
                    <div className="typo-normal-150 w-100">{referral.lastName ? referral.lastName : "N/A"}</div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Email Address:</div>
                    <div className="typo-normal-150 w-100">{referral.email ? referral.email : "N/A"}</div>
                    <div className="typo-strong-150 mt-2">Phone Number:</div>
                    <div className="typo-normal-150 w-100">{referral.phone ? referral.phone : "N/A"}</div>
                  </div>
                  <div className="col-xs-8">
                    <div className="typo-strong-150">Message:</div>
                    <div className="typo-normal-150 w-100">{referral.message ? referral.message : "N/A"}</div>
                  </div>
                </div>
              </div>
            </div>

            {renderPages()}

            {referral.importID && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Import Group</div>
                  <div className="typo-normal-125 mb-2">This referral is part of an import group</div>
                  <div onClick={() => navigate(`/referrals?importID=${referral.importID}`)} className="item grey typo-normal-150">
                    View all referrals from this import
                  </div>
                </div>
              </div>
            )}

            {referral.patientID && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Patient created</div>
                  <div className="typo-normal-125 mb-2">This form was used to create patient</div>
                  <Button onClick={() => navigate(`/patient/${referral.patientID}/card`)} color="blue" size="small" text="Open patient profile" />
                </div>
              </div>
            )}

            {referral.statusType === "TO_DO" && (
              <div className="card mt-4">
                <div className="card-header sm">
                  <div className="typo-strong-175">Create patient</div>
                  <div className="typo-normal-125 mb-2">This contact referral could be converted into patient or deleted</div>
                  <div onClick={() => navigate("/patient/create?referralID=" + referral.id)} className="item blue typo-normal-150">
                    Create patient
                  </div>
                  <div onClick={() => changeReferralStatus(template.statuses[2])} className="item grey typo-normal-150 ms-2">
                    Archive referral
                  </div>
                </div>
              </div>
            )}
          
            
          </div>
        
        </div>
      </div>
    </>
  );
}

export default Referral;
